import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, Pipette } from 'lucide-react';

interface ColorPickerProps {
  value: string;
  onChange: (color: string) => void;
  label?: string;
  isDark?: boolean;
  themeColors?: {
    background: string;
    primary: string;
    secondary: string;
    text: string;
    accent: string;
  };
}

// Common color presets
const COLOR_PRESETS = [
  // Row 1: Basic colors
  '#000000', '#ffffff', '#1f2937', '#374151', '#6b7280', '#9ca3af',
  // Row 2: Reds
  '#dc2626', '#ef4444', '#f87171', '#fca5a5', '#fee2e2', '#7f1d1d',
  // Row 3: Oranges
  '#ea580c', '#f97316', '#fb923c', '#fdba74', '#fed7aa', '#7c2d12',
  // Row 4: Yellows
  '#ca8a04', '#eab308', '#facc15', '#fde047', '#fef9c3', '#713f12',
  // Row 5: Greens
  '#16a34a', '#22c55e', '#4ade80', '#86efac', '#dcfce7', '#14532d',
  // Row 6: Blues
  '#2563eb', '#3b82f6', '#60a5fa', '#93c5fd', '#dbeafe', '#1e3a8a',
  // Row 7: Purples
  '#7c3aed', '#8b5cf6', '#a78bfa', '#c4b5fd', '#ede9fe', '#4c1d95',
  // Row 8: Pinks
  '#db2777', '#ec4899', '#f472b6', '#f9a8d4', '#fce7f3', '#831843',
];

export default function ColorPicker({
  value,
  onChange,
  label,
  isDark = true,
  themeColors,
}: ColorPickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [customColor, setCustomColor] = useState(value);
  const containerRef = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Update custom color when value changes
  useEffect(() => {
    setCustomColor(value);
  }, [value]);

  const handleColorSelect = (color: string) => {
    onChange(color);
    setIsOpen(false);
  };

  const handleCustomColorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const color = e.target.value;
    setCustomColor(color);
    onChange(color);
  };

  const handleHexInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let color = e.target.value;
    if (!color.startsWith('#')) {
      color = '#' + color;
    }
    setCustomColor(color);
    if (/^#[0-9A-Fa-f]{6}$/.test(color)) {
      onChange(color);
    }
  };

  return (
    <div ref={containerRef} className="relative">
      {label && (
        <label className={`block text-xs font-medium mb-2 ${
          isDark ? 'text-text-secondary' : 'text-slate-600'
        }`}>
          {label}
        </label>
      )}

      {/* Color Preview Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-2 px-3 py-2 rounded-lg border w-full ${
          isDark
            ? 'bg-dark-700 border-dark-600 hover:border-dark-500'
            : 'bg-white border-slate-200 hover:border-slate-300'
        }`}
      >
        <div
          className="w-6 h-6 rounded-md border border-white/20 shadow-inner"
          style={{ backgroundColor: value }}
        />
        <span className={`text-sm font-mono ${isDark ? 'text-white' : 'text-slate-900'}`}>
          {value.toUpperCase()}
        </span>
        <Pipette className={`w-4 h-4 ml-auto ${isDark ? 'text-text-muted' : 'text-slate-400'}`} />
      </button>

      {/* Color Picker Dropdown */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 4, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 4, scale: 0.95 }}
            className={`absolute z-50 mt-2 p-3 rounded-xl shadow-xl border ${
              isDark
                ? 'bg-dark-800 border-dark-600'
                : 'bg-white border-slate-200'
            }`}
            style={{ width: '240px' }}
          >
            {/* Theme Colors (if provided) */}
            {themeColors && (
              <>
                <p className={`text-xs font-medium mb-2 ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>
                  Theme Colors
                </p>
                <div className="flex gap-2 mb-3">
                  {Object.entries(themeColors).map(([name, color]) => (
                    <button
                      key={name}
                      onClick={() => handleColorSelect(color)}
                      className={`w-8 h-8 rounded-lg border-2 transition-transform hover:scale-110 ${
                        value === color ? 'ring-2 ring-purple-500 ring-offset-2' : ''
                      }`}
                      style={{
                        backgroundColor: color,
                        borderColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
                      }}
                      title={name}
                    >
                      {value === color && (
                        <Check className="w-4 h-4 mx-auto" style={{
                          color: isLightColor(color) ? '#000' : '#fff'
                        }} />
                      )}
                    </button>
                  ))}
                </div>
                <div className={`border-t mb-3 ${isDark ? 'border-dark-600' : 'border-slate-200'}`} />
              </>
            )}

            {/* Color Presets Grid */}
            <p className={`text-xs font-medium mb-2 ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>
              Preset Colors
            </p>
            <div className="grid grid-cols-6 gap-1.5 mb-3">
              {COLOR_PRESETS.map((color) => (
                <button
                  key={color}
                  onClick={() => handleColorSelect(color)}
                  className={`w-8 h-8 rounded-md border transition-transform hover:scale-110 ${
                    value === color ? 'ring-2 ring-purple-500 ring-offset-1' : ''
                  }`}
                  style={{
                    backgroundColor: color,
                    borderColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
                  }}
                >
                  {value === color && (
                    <Check className="w-3 h-3 mx-auto" style={{
                      color: isLightColor(color) ? '#000' : '#fff'
                    }} />
                  )}
                </button>
              ))}
            </div>

            {/* Custom Color Input */}
            <div className={`border-t pt-3 ${isDark ? 'border-dark-600' : 'border-slate-200'}`}>
              <p className={`text-xs font-medium mb-2 ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>
                Custom Color
              </p>
              <div className="flex gap-2">
                <input
                  type="color"
                  value={customColor}
                  onChange={handleCustomColorChange}
                  className="w-10 h-10 rounded-lg cursor-pointer border-0 bg-transparent"
                />
                <input
                  type="text"
                  value={customColor}
                  onChange={handleHexInputChange}
                  placeholder="#000000"
                  maxLength={7}
                  className={`flex-1 px-3 py-2 rounded-lg border text-sm font-mono ${
                    isDark
                      ? 'bg-dark-700 border-dark-600 text-white'
                      : 'bg-white border-slate-200 text-slate-900'
                  }`}
                />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// Helper function to determine if a color is light
function isLightColor(color: string): boolean {
  const hex = color.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  return brightness > 155;
}
